import {
  Button,
  Grid,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { permitBuildApiRef } from '../../../../apis/permitToBuildApi';
import { useStyles } from '../styles';
import BackButton from '../../../utils/backButton';
import { HelpComponent } from '../../../utils/helpComponent';
import HELP_URL from '../../../utils/helpLinkConstant';
import TicketFilters from '../Filters/TicketFilters';
import Search from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import { useLocation, useNavigate } from 'react-router-dom';
import TicketPopup from '../popups/TicketPopup';
import CommentsPopup from '../popups/CommentsPopup';
import MappedAssetsPopup from '../popups/MappedAssetsPopup';
import { useGetAllTickets } from '../hooks/useGetAllTickets';
import ManageTicketsTable from './ManageTicketsTable';
import { useTickets } from '../hooks/useTickets';
import { useFilteredTickets } from '../hooks/useFilteredTickets';
import TicketCard from './TicketCard';
import TableViewOutlinedIcon from '@material-ui/icons/TableViewOutlined';
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined';
import RunStatePopup from '../popups/RunStatePopup';

const ManageTickets = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [toggleView, setToggleView] = useState('card');
  const [searchText, setSearchText] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };
  const p2bApi = useApi(permitBuildApiRef);

  const { tickets, refreshTickets, loading } = useGetAllTickets(true);
  const { addConfig, saveComment, deleteTicket } = useTickets(false);

  const handleViewToggle = () => {
    setToggleView(prevView => (prevView === 'card' ? 'table' : 'card'));
  };

  const [filterOption, setFilterOption] = useState({
    lob: 'All',
    region: 'All regions',
    owner: 'All',
  });

  const [lob, setLob] = useState<any>([]);
  const [region, setRegion] = useState<any>([]);
  const [owner, setOwner] = useState<any>([]);

  useEffect(() => {
    if (location.state?.refresh && tickets?.data) {
      refreshTickets();
      navigate(location.pathname, { replace: true });
    }
  }, [tickets, location.state?.refresh]);

  useEffect(() => {
    if (tickets && tickets?.data?.length > 0) {
      setLob([
        'All',
        ...new Set(
          tickets?.data
            ?.map((ticket: any) => ticket.ticket.tower)
            .filter(Boolean),
        ),
      ]);
      setRegion([
        'All regions',
        ...new Set(
          tickets?.data
            ?.map((ticket: any) => ticket.ticket.region)
            .filter(Boolean),
        ),
      ]);
      setOwner([
        'All',
        ...new Set(
          tickets?.data
            ?.map((ticket: any) => ticket.ticket.assigned_to)
            .filter(Boolean),
        ),
      ]);
    }
  }, [tickets]);

  const emptyPopupState = {
    data: {},
    open: false,
    onClose: () => {},
    onSubmit: (formData: any) => {},
    onDelete: () => {},
    type: '',
  };

  const [popupState, setPopupState] = useState({
    ...emptyPopupState,
  });

  const handleTicketClick = (
    ticketId: string,
    ticketNumber: string,
    description: string,
  ) => {
    navigate(`${ticketId}`, {
      state: {
        ticketId,
        ticketNumber,
        description,
      },
    });
  };

  const { filteredTickets } = useFilteredTickets(
    tickets,
    filterOption,
    searchText,
  );

  const handleClosePopup = () => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: false,
      };
    });
  };

  const handleSaveTicket = (type: string, formData: any) => {
    switch (type) {
      case 'ticket':
        addConfig(formData).then(() => refreshTickets());
        break;
      case 'comment':
        saveComment(formData).then(() => refreshTickets());
        break;
      default:
        console.log('Outside switch');
        break;
    }
    handleClosePopup();
  };

  const handleDeleteTicket = (id: any) => {
    deleteTicket(id).then(() => refreshTickets());
    handleClosePopup();
  };

  const handleAddTicket = () => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: true,
        type: 'add',
        onClose: handleClosePopup,
        onSubmit: handleSaveTicket,
      };
    });
  };

  const handleEditTicket = (data: any) => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: true,
        data: data,
        type: 'editTicket',
        onClose: handleClosePopup,
        onDelete: handleDeleteTicket,
        onSubmit: handleSaveTicket,
      };
    });
  };

  const handleCommentClicked = (data: any) => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: true,
        data: data,
        type: 'comment',
        onClose: handleClosePopup,
        onSubmit: handleSaveTicket,
      };
    });
  };

  const handleMappedAssetsClicked = (data: any) => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: true,
        data: data,
        type: 'mappedAssets',
        onClose: handleClosePopup,
      };
    });
  };

  const handleTicketRunClicked = (ticket: any) => {
    setPopupState(prevState => {
      return {
        ...prevState,
        open: true,
        data: {
          title: `Run ticket no: ${ticket.ticket.snow_ticket_number}`,
        },
        type: 'ticketRun',
        onClose: handleClosePopup,
        onSubmit: () => {
          p2bApi
            .triggerTicketRun(ticket.ticket.id)
            .then(() => refreshTickets());

          handleClosePopup();
        },
      };
    });
  };
  return (
    <>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          className={classes.flexSpacebetween}
        >
          <BackButton />
          <HelpComponent
            helpUrl={HELP_URL.PERFORMANCE_TEST_MANAGECONFIG_PAGE}
          />
        </Grid>
      </Grid>
      {loading ? (
        <Grid container>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <LinearProgress />
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={1} lg={2}>
              <h1>Tickets</h1>
            </Grid>
            <Grid item xs={12} sm={6} md={11} lg={10}>
              <div
                className={`${!isExtraSmallScreen ? classes.flexEnd : ''} ${
                  classes.gap1
                }`}
              >
                <TicketFilters
                  filterOption={filterOption}
                  setFilterOption={setFilterOption}
                  lob={lob}
                  region={region}
                  owner={owner}
                />
                {!isMobile && (
                  <TextField
                    variant="outlined"
                    type="search"
                    className={classes.searchInTicketsPage}
                    value={searchText}
                    onChange={handleSearchChange}
                    placeholder="Search tickets"
                    InputProps={{ endAdornment: !searchText && <Search /> }}
                  />
                )}
                {!isSmallScreen && (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      classes={{ label: classes.buttonLabel }}
                      onClick={handleAddTicket}
                    >
                      <AddIcon />
                      <span>Add Ticket</span>
                    </Button>
                    {/* <Button
                      variant="contained"
                      color="primary"
                      classes={{ label: classes.buttonLabel }}
                      onClick={handleViewToggle}
                    >
                      {toggleView === 'card' ? (
                        <TableViewOutlinedIcon />
                      ) : (
                        <ListAltOutlinedIcon />
                      )}
                      <span>{toggleView === 'card' ? 'Table' : 'Card'}</span>
                    </Button> */}
                  </>
                )}
              </div>
            </Grid>
          </Grid>
          {isSmallScreen && (
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="primary"
                  classes={{ label: classes.buttonLabel }}
                  onClick={handleAddTicket}
                  fullWidth
                >
                  <AddIcon />
                  <span>Add Ticket</span>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="primary"
                  classes={{ label: classes.buttonLabel }}
                  onClick={handleViewToggle}
                  fullWidth
                >
                  {toggleView === 'card' ? (
                    <TableViewOutlinedIcon />
                  ) : (
                    <ListAltOutlinedIcon />
                  )}
                  <span>{toggleView === 'card' ? 'Table' : 'Card'}</span>
                </Button>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={12}>
              {isMobile && (
                <TextField
                  variant="outlined"
                  type="search"
                  fullWidth
                  className={`${classes.searchInTicketsPage} ${classes.marginBottomSmall}`}
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search tickets"
                  InputProps={{ endAdornment: !searchText && <Search /> }}
                />
              )}
            </Grid>
          </Grid>
          {/* {toggleView === 'card' ? ( */}
          <Grid container spacing={2}>
            {filteredTickets?.data?.map((ticket: any) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={ticket.ticket.id}>
                <TicketCard
                  ticket={ticket} // Pass single ticket instead of all tickets
                  handleTicketClick={handleTicketClick}
                  handleMappedAssetsClicked={handleMappedAssetsClicked}
                  handleCommentClicked={handleCommentClicked}
                  handleEditTicket={handleEditTicket}
                  handleRunTicket={handleTicketRunClicked}
                />
              </Grid>
            ))}
            {filteredTickets?.data?.length === 0 && (
              <Typography>No tickets found.</Typography>
            )}
          </Grid>
          {/* ) : (
            <ManageTicketsTable
              tickets={filteredTickets}
              handleTicketClick={handleTicketClick}
              handleMappedAssetsClicked={handleMappedAssetsClicked}
              handleCommentClicked={handleCommentClicked}
            />
          )} */}
        </>
      )}
      {popupState?.open && popupState?.type === 'add' && (
        <TicketPopup {...popupState} />
      )}
      {popupState?.open && popupState?.type === 'editTicket' && (
        <TicketPopup {...popupState} />
      )}
      {popupState?.open && popupState?.type === 'comment' && (
        <CommentsPopup {...popupState} />
      )}
      {popupState?.open && popupState?.type === 'mappedAssets' && (
        <MappedAssetsPopup {...popupState} />
      )}
      {popupState?.open && popupState?.type === 'ticketRun' && (
        <RunStatePopup {...popupState} />
      )}
    </>
  );
};

export default ManageTickets;
